import { clsx } from 'clsx';
import type { ReactNode } from 'react';

export interface CheckboxProps {
  /** Whether the checkbox is checked */
  checked: boolean;
  /** Called when the checkbox state changes */
  onChange: (checked: boolean) => void;
  /** Label content - supports ReactNode for inline links */
  label: ReactNode;
  /** Additional className for the container */
  className?: string;
  /** Disabled state */
  disabled?: boolean;
}

/**
 * Checkbox - Atom component for checkbox inputs with label
 *
 * Supports ReactNode labels to allow inline links (e.g., T&C links).
 * Uses a custom styled checkbox with Dorsia design tokens.
 */
export function Checkbox({ checked, onChange, label, className, disabled = false }: CheckboxProps) {
  return (
    <label
      className={clsx(
        'flex cursor-pointer items-start gap-3',
        disabled && 'cursor-not-allowed opacity-50',
        className
      )}
    >
      <span className="relative mt-0.5 flex-shrink-0">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          className="peer sr-only"
        />
        <span
          className={clsx(
            'flex h-5 w-5 items-center justify-center rounded border transition-colors',
            checked
              ? 'border-dorsia-black bg-dorsia-black'
              : 'border-dorsia-border bg-dorsia-white'
          )}
          aria-hidden="true"
        >
          {checked && (
            <svg width="12" height="10" viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 5L4.5 8.5L11 1.5" stroke="#FDFDFD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </span>
      </span>
      <span className="font-sailec text-dorsia-black text-sm leading-5">{label}</span>
    </label>
  );
}
